import { h, Fragment } from "preact";
import { useRef } from "preact/hooks";

const PET_SERVICES = new Set([
  "pet",
  "pet_second_dog",
  "pet_third_dog",
  "cleaning_with_pet",
  "cleaning_per_pet",
]);

const enumValue = (value) => value?.value ?? value;
const isPetService = (serviceName) => PET_SERVICES.has(serviceName);

const FINAL_PAYMENT_LABEL_KEYS = {
  ARRANGEMENT: "price.details.payment_arrangement",
  ON_ARRIVAL: "price.details.payment_on_arrival",
  AFTER_BOOKING: "price.details.payment_after_booking",
};

function amountToHuman(val) {
  if (
    typeof window !== "undefined" &&
    typeof window.amountToHuman === "function"
  ) {
    return window.amountToHuman(val);
  }
  const locale = global.Translator?.locale === "en" ? "en-GB" : "de-DE";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  }).format(val || 0);
}

export default function PriceDetailsModal({
  discounts = { total: 0, items: {} },
  days = 0,
  gross = 0,
  rentalTotal = 0,
  perNight = 0,
  cleaning = 0,
  unit = {},
  hasIncludedTaxes = false,
  kurtaxe = 0,
  businessOnly = false,
  unitsNeeded = 1,
  persons = 1,
  dogs = 0,
  seasons = [],
  services = [],
  infoServices = [],
  hasTourismTax = false,
  utilities = [],
}) {
  const modalRef = useRef(null);

  const openModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (modalRef.current) {
      modalRef.current.showModal();
    }
  };

  const closeModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      closeModal();
    }
  };

  const t = (key, params = {}) => {
    return global.Translator?.trans
      ? global.Translator.trans(key, params, "js")
      : key;
  };

  const tc = (key, count, params = {}) => {
    return global.Translator?.transChoice
      ? global.Translator.transChoice(key, count, { count, ...params }, "js")
      : `${count}`;
  };

  const unitType = enumValue(unit?.type || "").toUpperCase();
  const finalPayment = enumValue(unit?.final_payment);
  const portal =
    typeof window !== "undefined" && window.portal ? window.portal : "PN";

  const includedServices = services.filter((srv) => srv.included);
  const additionalServices = services.filter((srv) => !srv.included);
  const utilityNames = utilities
    .map((name) => t(`rental.service.service_name.${name}`))
    .join(", ");
  const hasConsumptionCosts = infoServices.length > 0 || utilities.length > 0;
  const finalPaymentLabel = () => {
    if (finalPayment === "BEFORE_ARRIVAL" && unit.final_payment_days) {
      return `${unit.final_payment_days} ${t("price.details.days_before_arrival")}`;
    }
    return t(
      FINAL_PAYMENT_LABEL_KEYS[finalPayment] ??
        "price.details.payment_before_arrival",
    );
  };

  const includedServiceLabel = (srv) => {
    if (
      isPetService(srv.name) &&
      ["per_day", "per_pet_per_day"].includes(srv.unitName)
    ) {
      const name = t(`rental.service.service_name.${srv.name}`);
      return `${name} (${tc("general.dogs", dogs)} × ${tc("general.nights", days)})`;
    }

    return srv.label;
  };

  const weekCountLabel = (nights) => {
    const weeks = Math.floor(nights / 7);
    return `${weeks} ${t(`price.details.${weeks === 1 ? "week" : "weeks"}`)}`;
  };

  return (
    <Fragment>
      <button
        type="button"
        className="text-sm text-brand-600 hover:text-brand-800 underline font-medium cursor-pointer ml-auto transition-colors"
        onClick={openModal}
      >
        {t("price.details.headline")}
      </button>

      <dialog
        ref={modalRef}
        onClick={handleBackdropClick}
        className="backdrop:bg-black/50 bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full m-auto open:flex open:flex-col text-brandcopy-900 border-0 z-50 fixed inset-0 overflow-y-auto"
      >
        <div className="flex justify-between items-center pb-3 border-b border-brandcopy-100">
          <h3 className="font-bold text-lg text-brandcopy-900">
            {t("price.details.headline")}
          </h3>
          <button
            type="button"
            className="text-brandcopy-400 hover:text-brandcopy-700 rounded-full p-1 transition focus:outline-none"
            onClick={closeModal}
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mt-4 space-y-3 text-sm">
          {(businessOnly || t("price.hint")) && (
            <p className="text-xs text-brandcopy-600 pb-1">
              {businessOnly && (
                <span className="font-medium text-amber-700 mr-1">
                  {t("price.businessOnly")}
                </span>
              )}
              {t("price.hint")}
            </p>
          )}

          {seasons && seasons.length > 1 ? (
            <div className="border-b border-brandcopy-100 pb-2 space-y-1">
              <div className="text-sm font-semibold text-brandcopy-700 mb-1">
                {t("booking.price_seasons") || "Saisonpreise"}
              </div>
              {seasons.map((s, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center text-sm text-brandcopy-700"
                >
                  <span>
                    {s.name} ({s.nights} &times; {amountToHuman(s.rate)}
                    {s.weekRate > 0 &&
                      `; ${weekCountLabel(s.nights)} à ${amountToHuman(s.weekRate)}`})
                  </span>
                  <span className="font-medium text-brandcopy-900">
                    {amountToHuman(s.total)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex justify-between items-center gap-2">
              <div className="flex flex-wrap text-nowrap text-brandcopy-700">
                <span>{amountToHuman(perNight)}</span>
                {seasons && seasons.length === 1 && seasons[0].name && (
                  <span className="text-xs text-brandcopy-500 self-center ml-1">
                    ({seasons[0].name})
                  </span>
                )}
                <span>&nbsp;&times;&nbsp;{tc("general.nights", days)}</span>
                <span>
                  &nbsp;&times;&nbsp;
                  {unitType === "SHARED"
                    ? tc("price.details.persons", persons)
                    : `${unitsNeeded} ${t(
                        `units.type.${unitsNeeded > 1 ? "plural" : "singular"}.${unitType || "APARTMENT"}`
                      )}`}
                </span>
              </div>
              <div className="font-semibold text-right">
                {amountToHuman(rentalTotal)}
              </div>
            </div>
          )}

          {discounts?.total > 0 && (
            <div className="flex justify-between items-start gap-2 text-green-700">
              <div className="flex-1">
                <div>{t("units.discounts.applied")}</div>
                <ul className="list-disc list-inside text-xs text-brandcopy-600 mt-1 space-y-0.5">
                  {Object.keys(discounts.items || {}).map(
                    (d, index) =>
                      discounts.items[d] > 0 && (
                        <li key={index}>{t(`units.discounts.type.${d}`)}</li>
                      )
                  )}
                </ul>
              </div>
              <div className="font-semibold text-right text-green-700 whitespace-nowrap">
                &minus; {amountToHuman(discounts.total)}
              </div>
            </div>
          )}

          {cleaning > 0 && (
            <div className="flex justify-between items-center gap-2 text-brandcopy-700">
              <div>{t("units.cleaning")}</div>
              <div className="font-semibold text-right">
                {amountToHuman(cleaning)}
              </div>
            </div>
          )}

          {includedServices.length > 0 && (
            <div className="border-y border-brandcopy-100 my-1 space-y-1">
              <div className="text-sm font-semibold text-brandcopy-700 mb-1">
                {t("rental.service.required_services_headline")}
              </div>
              {includedServices.map((srv, idx) => (
                <div
                  key={srv.id || idx}
                  className="flex justify-between items-center text-sm text-brandcopy-700"
                >
                  <span>{includedServiceLabel(srv)}</span>
                  <span className="font-medium text-brandcopy-900">
                    {amountToHuman(srv.total)}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between items-center gap-2 text-brandcopy-700">
            <div>
              {t(`${portal}.breadcrumb.home`)} {t("price.details.service_fee")}
            </div>
            <div className="font-semibold text-green-700 text-right">
              {t("general.always")} {amountToHuman(0)}
            </div>
          </div>

          <hr className="my-2 border-brandcopy-200" />

          <div className="flex justify-between items-center text-base font-bold text-brandcopy-900">
            <div>{t("price.total")}*</div>
            <div className="text-right text-lg text-brand-700">
              {amountToHuman(gross)}
            </div>
          </div>

          {hasIncludedTaxes && (
            <div className="text-right text-xs text-brandcopy-600">
              {t("price.details.tax_included")}
            </div>
          )}

          {hasTourismTax && (
            <div className="text-right text-xs text-brandcopy-600">
              {t("price.details.tourism_tax_extra")}
            </div>
          )}

          {kurtaxe > 0 && (
            <div className="flex justify-between items-center gap-2 text-brandcopy-700">
              <div>{t("price.details.kurtaxe.label")}</div>
              <div className="font-semibold text-right">
                + {amountToHuman(kurtaxe)}
              </div>
            </div>
          )}

          {additionalServices.length > 0 && (
            <div className="border-y border-brandcopy-100 py-2 space-y-1">
              <div className="text-sm font-semibold text-brandcopy-700 mb-1">
                {t("price.details.additional_costs")}
              </div>
              {additionalServices.map((srv, idx) => (
                <div
                  key={srv.id || idx}
                  className="flex justify-between items-center gap-2 text-sm text-brandcopy-700"
                >
                  <span>
                    {srv.label}
                    {!srv.required && ` (${t("rental.service.optional")})`}
                  </span>
                  <span className="text-right">
                    {amountToHuman(srv.grossAmount)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {unit?.deposit !== null &&
            unit?.deposit !== undefined &&
            unit?.deposit !== "" && (
            <div className="flex justify-between items-center gap-2 text-brandcopy-700 text-sm">
              <div>{t("units.deposit")}</div>
              <div className="font-semibold text-right">
                {amountToHuman(unit.deposit)}
              </div>
            </div>
          )}

          {unit?.prepayment !== null && unit?.prepayment !== undefined && (
            <div className="flex justify-between items-center gap-2 text-brandcopy-700 text-sm">
              <div>{t("price.details.prepayment")}</div>
              <div className="font-medium text-right">
                {unit.prepayment === 0
                  ? t("price.details.no_prepayment")
                  : unit.prepayment === 1
                    ? `${unit.prepayment_amount || 0}% ${t("price.details.of_rent")}`
                    : amountToHuman(unit.prepayment_amount || 0)}
              </div>
            </div>
          )}

          {unit?.final_payment && (
            <div className="flex justify-between items-center gap-2 text-brandcopy-700 text-sm">
              <div>{t("price.details.final_payment")}</div>
              <div className="font-medium text-right">{finalPaymentLabel()}</div>
            </div>
          )}

          <div className="text-brandcopy-500 text-xs pt-2 border-t border-brandcopy-100 space-y-1">
            <p>{t("price.calculation.hint")}</p>
            {hasConsumptionCosts && (
              <p>
                ** {t("price.details.utilities_after_stay", {
                  utilities:
                    utilityNames || t("rental.service.service_name.utilities"),
                })}
              </p>
            )}
          </div>
        </div>
      </dialog>
    </Fragment>
  );
}
